import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone } from "lucide-react";
import { fetchHouseAds, mediaUrl } from "@/lib/api";

const CLIENT = process.env.REACT_APP_ADSENSE_CLIENT;

// Cache house-ad requests per placement with a short TTL so each AdSlot
// doesn't refetch on every mount, while still picking up admin changes.
const houseCache = {};
const TTL = 20000;
const getHouseAds = (placement) => {
  const entry = houseCache[placement];
  if (entry && Date.now() - entry.t < TTL) return entry.p;
  const p = fetchHouseAds(placement).catch(() => []);
  houseCache[placement] = { p, t: Date.now() };
  return p;
};
// Called by the Ad Manager after create / toggle / delete.
export const invalidateHouseAds = () => {
  for (const k of Object.keys(houseCache)) delete houseCache[k];
};

/**
 * Ad slot with two fill sources:
 * 1. House ads (manually uploaded via the Ad Manager) — shown first.
 * 2. Google AdSense (once approved & a slot id is passed) — fallback.
 */
export function AdSlot({ slot, variant = "leaderboard", className = "" }) {
  const pushed = useRef(false);
  const [house, setHouse] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    getHouseAds(variant).then((ads) => {
      if (!active) return;
      setHouse(ads && ads.length ? ads[Math.floor(Math.random() * ads.length)] : null);
      setLoaded(true);
    });
    return () => {
      active = false;
    };
  }, [variant]);

  useEffect(() => {
    if (loaded && !house && slot && !pushed.current) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        pushed.current = true;
      } catch (e) {
        /* adsbygoogle not ready */
      }
    }
  }, [loaded, house, slot]);

  const heights = {
    leaderboard: "min-h-[110px]",
    rectangle: "min-h-[260px]",
    infeed: "min-h-[220px]",
  };
  // Fixed media height per variant so banners crop cleanly instead of
  // stretching the slot to the image's intrinsic aspect ratio.
  const mediaH = {
    leaderboard: "h-[130px]",
    rectangle: "h-[250px]",
    infeed: "h-[200px]",
  };

  return (
    <div
      data-testid={`ad-slot-${variant}`}
      className={`relative bg-card border border-white/10 overflow-hidden ${heights[variant]} ${className} flex flex-col`}
    >
      <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-muted-foreground px-3 py-1.5 border-b border-white/10">
        Advertisement
      </span>

      {house ? (
        <a
          href={house.link_url}
          target="_blank"
          rel="noopener noreferrer sponsored"
          data-testid={`house-ad-${house.id}`}
          className={`relative block w-full ${mediaH[variant]} group`}
          title={house.title}
        >
          <img
            src={mediaUrl(house.image)}
            alt={house.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <span className="absolute bottom-2 left-2 font-mono text-[9px] uppercase tracking-widest bg-black/70 text-white px-2 py-0.5">
            {house.title}
          </span>
        </a>
      ) : slot ? (
        <ins
          className="adsbygoogle"
          style={{ display: "block", width: "100%", flex: 1 }}
          data-ad-client={CLIENT}
          data-ad-slot={slot}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      ) : (
        <Link
          to="/advertise"
          data-testid={`ad-promo-${variant}`}
          className="flex-1 flex flex-col items-center justify-center text-center px-4 py-6 group hover:bg-secondary transition-colors"
        >
          <Megaphone className="w-5 h-5 text-signal mb-2 group-hover:scale-110 transition-transform" />
          <span className="font-anton text-lg uppercase tracking-tight leading-none group-hover:text-signal transition-colors">
            Your Ad Here
          </span>
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground mt-1.5">
            From <span className="text-signal">$250/mo</span> · Advertise with NERO SUN →
          </span>
        </Link>
      )}
    </div>
  );
}
